import { useMutation, useQueryClient } from '@tanstack/react-query';
import { put } from '@/shared/api/api-client';
import { invalidateIfNotInFlight } from '@/patients/lib/invalidate-if-not-in-flight';
import type { UpdateFamilyMedicalHistoryBody } from '@/types/patient-chart-api';

/**
 * PUT /api/Patients/familyMedicalHistory/{id}
 *
 * Shared endpoint for Edit and soft-Delete on Family Medical History.
 * Wire body curl-verified by Andrew 2026-05-20. See
 * `UpdateFamilyMedicalHistoryBody` type doc for the round-trip rules
 * (most notably: per-code-element `performerDetails` round-trip preserved
 * for unchanged rows, current user stamped on newly-added rows only;
 * `createdBy`/`createdDate` preserved from original record).
 *
 * Cache: invalidates `['patient-family-history', patientId]`. SignalR
 * `UpdatePatientChat` "FamilyHistory" event also fires for concurrent
 * sessions.
 */

interface UpdateFamilyMedicalHistoryParams {
  patientId: string;
  familyHistoryId: string;
  body: UpdateFamilyMedicalHistoryBody;
}

export function useUpdateFamilyMedicalHistory() {
  const qc = useQueryClient();
  return useMutation<string, Error, UpdateFamilyMedicalHistoryParams>({
    mutationFn: ({ familyHistoryId, body }) =>
      put<string>(
        `/api/Patients/familyMedicalHistory/${familyHistoryId}`,
        body,
      ),
    onSuccess: (_data, { patientId }) => {
      invalidateIfNotInFlight(qc, ['patient-family-history', patientId]);
    },
  });
}
